import { useState } from 'react';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { DIVISION_ROLE_LABEL, type DivisionRole } from '../../entities/pipeline';
import { divisionApi, type DivisionOverview } from '../../shared/api/division.api';
import { useCachedQuery } from '../../shared/lib/queryCache';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

const ROLES: DivisionRole[] = ['head', 'content_maker', 'editor', 'moderator', 'distributor'];

/** Роли, которые публикуют. Остальным переключатель показывать не о чем. */
const PUBLISHING: readonly DivisionRole[] = ['head', 'content_maker', 'distributor'];

/**
 * Состав отдела: кто есть, с какой ролью, в какой группе и кому можно
 * выкладывать.
 *
 * Право публиковать стоит прямо в строке, а не в отдельном окне настроек:
 * это то, что руководитель меняет чаще всего, когда человек уходит в отпуск
 * или его временно снимают с паблика.
 */
export function MembersTab({
  divisionId, overview, onChanged, onPatch,
}: {
  divisionId: string;
  overview: DivisionOverview | undefined;
  onChanged: () => void;
  /** Показать правку сразу, не дожидаясь сервера. */
  onPatch?: (fn: (o: DivisionOverview) => DivisionOverview) => void;
}): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);

  const members = overview?.members ?? [];
  // Где человек работает — направления, куда его поставили (08.10.2026).
  // Команд на экране больше нет: доступ и ролики решают направления.
  const projects = (overview?.projects ?? []).filter((p) => !p.archived);
  const directionsOf = (userId: string) =>
    projects.filter((p) => p.memberIds?.some((m) => m.userId === userId)).map((p) => p.name);

  async function change(userId: string, patch: Parameters<typeof divisionApi.updateMember>[2]): Promise<void> {
    // Переключатель срабатывает сразу; сервер догоняет в фоне (08.10.2026).
    onPatch?.((o) => ({ ...o, members: o.members.map((m) => (m.id === userId ? { ...m, ...patch } as typeof m : m)) }));
    try {
      await divisionApi.updateMember(divisionId, userId, patch);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      onChanged();
    }
  }

  async function remove(userId: string, name: string | null): Promise<void> {
    setBusy(userId);
    try {
      await divisionApi.removeMember(divisionId, userId);
      toast.success(`${name ?? 'Человек'} больше не в отделе`);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <InviteDialog divisionId={divisionId} onAdded={onChanged} />
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Человек</TableHead>
              <TableHead>Роль</TableHead>
              <TableHead>Направления</TableHead>
              <TableHead>Доступ</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2.5">
                    <Avatar className="size-8">
                      <AvatarImage src={member.avatarUrl ?? undefined} alt="" />
                      <AvatarFallback className="text-xs">{(member.name ?? member.username ?? '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    {member.name ?? member.username}
                  </span>
                </TableCell>

                <TableCell>
                  <Select
                    value={member.role}
                    onValueChange={(role) => change(member.id, { role: role as DivisionRole })}
                    disabled={busy === member.id}
                  >
                    <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((role) => (
                        <SelectItem key={role} value={role}>{DIVISION_ROLE_LABEL[role]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>

                <TableCell className="text-sm">
                  {member.role === 'head' || member.role === 'moderator' ? (
                    <span className="text-muted-foreground">всё отделение</span>
                  ) : directionsOf(member.id).length ? (
                    directionsOf(member.id).join(', ')
                  ) : (
                    <span className="text-muted-foreground">нигде — добавьте на вкладке «Направления»</span>
                  )}
                </TableCell>

                <TableCell>
                  {PUBLISHING.includes(member.role) ? (
                    <label className="flex items-center gap-2 text-sm">
                      <Switch
                        checked={member.canPublish}
                        onCheckedChange={(value) => change(member.id, { canPublish: value })}
                        aria-label="Можно публиковать"
                      />
                      публикует
                    </label>
                  ) : member.role === 'editor' ? (
                    // Монтажёр (08.10.2026): проходит ли модератора (на аккаунтах с
                    // модерацией) и публикует ли сданное сам, без приёмки.
                    <div className="flex flex-col gap-1.5 text-sm">
                      <label className="flex items-center gap-2">
                        <Switch
                          checked={!member.skipModeration}
                          onCheckedChange={(value) => change(member.id, { skipModeration: !value })}
                          aria-label="Проходит модерацию"
                        />
                        модерация
                      </label>
                      <label className="flex items-center gap-2">
                        <Switch
                          checked={Boolean(member.selfPublish)}
                          onCheckedChange={(value) => change(member.id, { selfPublish: value })}
                          aria-label="Публикует сам"
                        />
                        публикует сам
                      </label>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy === member.id}
                    onClick={() => remove(member.id, member.name)}
                  >
                    {busy === member.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Убрать'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InviteDialog({
  divisionId, onAdded,
}: {
  divisionId: string;
  onAdded: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<DivisionRole>('editor');
  const [sending, setSending] = useState(false);

  const { data } = useCachedQuery(
    `division:${divisionId}:candidates`,
    () => divisionApi.getCandidates(divisionId),
    { enabled: open },
  );

  async function submit(): Promise<void> {
    if (!userId) return;
    setSending(true);
    try {
      await divisionApi.addMember(divisionId, {
        userId, role,
      });
      setOpen(false);
      setUserId('');
      onAdded();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не вышло');
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><UserPlus className="h-4 w-4" />Позвать в отделение</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Позвать в отделение</DialogTitle></DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Человек</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger><SelectValue placeholder="Выберите сотрудника" /></SelectTrigger>
              <SelectContent>
                {(data?.candidates ?? []).map((person) => (
                  <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data && data.candidates.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Свободных сотрудников нет. Новые учётки заводит администратор платформы.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Роль</Label>
            <Select value={role} onValueChange={(value) => setRole(value as DivisionRole)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((item) => (
                  <SelectItem key={item} value={item}>{DIVISION_ROLE_LABEL[item]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            Потом добавьте человека в направления на вкладке «Направления» — там решается, что он видит и где работает.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={!userId || sending}>
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            Позвать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
